/**
 * OfficeBooksNav — the way into your own books, from your own office.
 *
 * ── The gap this closes ──
 * office-one made the office altitude carry NO sidebar at all: the books nav
 * belonged to an entity, and at the firm altitude there is no entity, so
 * `officeNoSidebar` deleted the whole rail. That was right about the altitude
 * and wrong about the consequence — a consultant standing in their office had
 * no navigation on the left at ALL, and the single door to their own books was
 * a row inside the office roster that you have to know is there. Every other
 * screen in Karr has a sidebar; this one had a blank margin.
 *
 * So the sidebar comes back at the office, shut until asked for. Shut, it is
 * not there at all: only SideDrawer's handle, halfway down the left edge, with
 * the word **Books** when you reach for it (sidecentre, 5 Oct 2026; it used to
 * be a 64px strip with the close arrow in its header). Open it and the whole ordinary books nav is there —
 * the consultant's own books, because at the office altitude the active org IS
 * the firm (see AppLayout's `officeNoSidebar`, which is `isPracticeOrg &&
 * practiceMode`, so the org you are standing in is your own practice).
 *
 * ── Why this is not wv-2's "This firm" toggle coming back ──
 * That toggle sat at the FOOT of the sidebar and switched the altitude, which
 * read as a second firm-altitude door beside the header's Office pill ("office
 * is this firm, why do we have this firm separately"). This switches nothing:
 * the altitude is still a fact about the route, the header pill is still the
 * only way up, and this is only the ordinary books nav being reachable instead
 * of deleted. Up is the pill; down is here.
 *
 * ── Why it wraps Sidebar rather than teaching Sidebar a new mode ──
 * Sidebar already carries three collapse behaviours (persisted `collapsed`, the
 * `autoCollapse` peek, `forceExpanded` for the mobile drawer) that every other
 * route in the app depends on. A fourth would have to interact with all three.
 * This owns its own open/shut state and hands Sidebar the one mode it already
 * has — so nothing outside the office altitude can change behaviour.
 *
 * @since office-books (2026-08-11)
 */
import { useState, useCallback } from 'react';
import { Sidebar, type NavZone, type NavItem } from './Sidebar';
import { BookOpen } from 'lucide-react';
import { SideDrawer } from './SideDrawer';

/** Remembered across visits: this is a working preference, not a peek. */
const OPEN_KEY = 'karr-office-books-open';

export function OfficeBooksNav({ zones, activeRoute, onNavigate, pinnableItems, firmName }: {
    zones: NavZone[];
    activeRoute: string;
    onNavigate: (href: string) => void;
    pinnableItems?: NavItem[];
    /** The firm whose books these are — named out loud, so nobody opens this
     *  expecting a client's ledgers. */
    firmName?: string | null;
}) {
    const [open, setOpen] = useState(() => {
        try { return localStorage.getItem(OPEN_KEY) === 'true'; } catch { return false; }
    });

    const set = useCallback((next: boolean) => {
        setOpen(next);
        try { localStorage.setItem(OPEN_KEY, String(next)); } catch { /* noop */ }
    }, []);

    // An office with no books nav to show (a staffer's `/my-work`, an org whose
    // modules are all off) gets no rail at all rather than a button that opens
    // an empty panel — a control that cannot do anything is worse than none.
    if (zones.length === 0) return null;

    // sidecentre (5 Oct 2026): shut, it is not there at all (no "Books" strip)
    // and the page gets the width; the handle halfway down the edge opens it.
    return (
        <SideDrawer
            open={open}
            onOpenChange={set}
            openLabel={firmName ? `Books — open ${firmName}'s books` : 'Books — open your own books'}
            closeLabel="Close the books nav"
            name="Books"
        >
            <div className="flex h-full w-60 flex-col border-r border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
                <div className="flex shrink-0 items-center gap-1.5 border-b border-stone-200 px-3 py-2.5 dark:border-stone-800">
                    <BookOpen size={15} className="shrink-0 text-teal-600 dark:text-teal-400" />
                    <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-stone-800 dark:text-stone-100">
                        {firmName ? `${firmName} · books` : 'Your books'}
                    </span>
                </div>
                {/* min-h-0 so the Sidebar's own overflow-y-auto scrolls INSIDE this
                    column instead of pushing the header off the top. */}
                <div className="min-h-0 flex-1">
                    <Sidebar
                        zones={zones}
                        activeRoute={activeRoute}
                        onNavigate={onNavigate}
                        forceExpanded
                        pinnableItems={pinnableItems}
                    />
                </div>
            </div>
        </SideDrawer>
    );
}

export default OfficeBooksNav;
